(() => {
  const root = document.documentElement;

  const setHeaderState = () => {
    root.classList.toggle('fg-scrolled', window.scrollY > 12);
  };

  const reveal = () => {
    const targets = document.querySelectorAll('[data-fg-reveal]');
    root.classList.add('fg-motion-ready');

    const sequencedGroups = [
      '.fg-product-grid',
      '.fg-category-grid',
      '.fg-home-trust-strip__grid',
      '.fg-pdp-details__grid',
      '.fg-info-grid',
      '.fg-journal-grid',
      '.fg-ambassador-perks',
      '.fg-paddle-finder__form',
      '.fg-paddle-finder__result'
    ];

    sequencedGroups.forEach((selector) => {
      document.querySelectorAll(selector).forEach((group) => {
        Array.from(group.children).forEach((child, index) => {
          child.style.setProperty('--fg-sequence-index', String(index));
        });
      });
    });

    document.querySelectorAll('.fg-comparison-table tbody tr, .fg-comparison-row').forEach((row, index) => {
      row.style.setProperty('--fg-sequence-index', String(index));
    });

    if (!('IntersectionObserver' in window)) {
      targets.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -2% 0px', threshold: 0.02 });

    targets.forEach((item) => observer.observe(item));
  };

  const wirePremiumMotion = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hero = document.querySelector('.fg-power-hero');
    if (hero && !reduceMotion) {
      let ticking = false;
      const updateHero = () => {
        const rect = hero.getBoundingClientRect();
        const viewport = window.innerHeight || 1;
        const progress = Math.min(1, Math.max(-1, (viewport * 0.5 - rect.top) / viewport));
        hero.style.setProperty('--fg-hero-depth', `${progress * -18}px`);
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateHero);
      }, { passive: true });
      updateHero();
    }

    if (!reduceMotion && 'IntersectionObserver' in window) {
      const liveObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-motion-visible', entry.isIntersecting);
        });
      }, { threshold: 0.16 });

      document.querySelectorAll('.fg-product-card, .fg-category-card, .fg-home-trust-strip__grid div, .fg-comparison-table-wrap, .fg-paddle-finder, .fg-panel-sheet').forEach((item) => {
        liveObserver.observe(item);
      });
    }
  };

  const wireCollectionSort = () => {
    document.querySelectorAll('[data-fg-sort]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.href = url.toString();
      });
    });
  };

  const wireCollectionFilters = () => {
    const drawer = document.querySelector('[data-fg-filter-drawer]');
    const openButton = document.querySelector('[data-fg-filter-open]');
    if (!drawer || !openButton) return;

    const closeButtons = drawer.querySelectorAll('[data-fg-filter-close]');
    const openDrawer = () => {
      root.classList.add('fg-filter-open');
      drawer.setAttribute('aria-hidden', 'false');
      openButton.setAttribute('aria-expanded', 'true');
    };
    const closeDrawer = () => {
      root.classList.remove('fg-filter-open');
      drawer.setAttribute('aria-hidden', 'true');
      openButton.setAttribute('aria-expanded', 'false');
      openButton.focus({ preventScroll: true });
    };

    openButton.addEventListener('click', openDrawer);
    closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.classList.contains('fg-filter-open')) {
        closeDrawer();
      }
    });
  };

  const showToast = (message) => {
    let toast = document.querySelector('[data-fg-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fg-toast';
      toast.setAttribute('data-fg-toast', '');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const closePanels = () => {
    root.classList.remove('fg-quick-add-open', 'fg-cart-drawer-open');
  };

  const closeHeaderPanels = () => {
    const menu = document.querySelector('[data-fg-mobile-menu]');
    const menuButton = document.querySelector('[data-fg-menu-toggle]');
    const search = document.querySelector('[data-fg-search-panel]');
    const searchButton = document.querySelector('[data-fg-search-toggle]');

    root.classList.remove('fg-menu-open', 'fg-search-open');
    if (menu) menu.setAttribute('aria-hidden', 'true');
    if (search) search.setAttribute('aria-hidden', 'true');
    if (menuButton) menuButton.setAttribute('aria-expanded', 'false');
    if (searchButton) searchButton.setAttribute('aria-expanded', 'false');
  };

  const wireHeaderPanels = () => {
    const menu = document.querySelector('[data-fg-mobile-menu]');
    const menuButton = document.querySelector('[data-fg-menu-toggle]');
    const search = document.querySelector('[data-fg-search-panel]');
    const searchButton = document.querySelector('[data-fg-search-toggle]');
    const searchInput = document.querySelector('[data-fg-search-input]');

    if (menuButton && menu) {
      menuButton.addEventListener('click', () => {
        const willOpen = !root.classList.contains('fg-menu-open');
        root.classList.remove('fg-search-open');
        root.classList.toggle('fg-menu-open', willOpen);
        menu.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
        menuButton.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (search) search.setAttribute('aria-hidden', 'true');
        if (searchButton) searchButton.setAttribute('aria-expanded', 'false');
      });

      menu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', closeHeaderPanels);
      });
    }

    if (searchButton && search) {
      searchButton.addEventListener('click', (event) => {
        event.preventDefault();
        const willOpen = !root.classList.contains('fg-search-open');
        root.classList.remove('fg-menu-open');
        root.classList.toggle('fg-search-open', willOpen);
        search.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
        searchButton.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (menu) menu.setAttribute('aria-hidden', 'true');
        if (menuButton) menuButton.setAttribute('aria-expanded', 'false');

        if (willOpen && searchInput) {
          window.setTimeout(() => searchInput.focus({ preventScroll: true }), 180);
        }
      });

      search.querySelectorAll('[data-fg-search-close]').forEach((button) => {
        button.addEventListener('click', closeHeaderPanels);
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeHeaderPanels();
    });
  };

  const ensurePanel = (type) => {
    const selector = `[data-fg-${type}]`;
    let panel = document.querySelector(selector);
    if (panel) return panel;

    panel = document.createElement('div');
    panel.className = `fg-${type}`;
    panel.setAttribute(`data-fg-${type}`, '');
    panel.innerHTML = `
      <button class="fg-panel-backdrop" type="button" data-fg-close-panel aria-label="Close"></button>
      <aside class="fg-panel-sheet" role="dialog" aria-modal="true">
      <button class="fg-panel-close" type="button" data-fg-close-panel aria-label="Close">&times;</button>
        <div data-fg-panel-content></div>
      </aside>
    `;
    document.body.appendChild(panel);
    return panel;
  };

  const getSelectedQuantity = (form) => {
    const selected = form.querySelector('[name="quantity"]:checked') || form.querySelector('[name="quantity"]');
    const value = Number(selected ? selected.value : 1);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const getProductKind = (form) => {
    const inputKind = form.querySelector('[data-fg-product-kind-value]');
    return form.dataset.fgProductKind || (inputKind && inputKind.value) || '';
  };

  const updateBundleProperty = (form) => {
    const property = form.querySelector('[data-fg-bundle-property]');
    if (!property) return '';

    const quantity = getSelectedQuantity(form);
    const productKind = getProductKind(form).toLowerCase();
    let offer = '';

    if (productKind.includes('paddle') && !productKind.includes('youth')) {
      offer = quantity >= 2
        ? 'Buy 2 paddles, get 1 kids paddle free'
        : 'Buy 1 paddle, get 1 pickleball free';
    }

    property.value = offer;
    return offer;
  };

  const getGiftLabel = (offer) => {
    if (!offer) return '';
    const lower = offer.toLowerCase();
    if (lower.includes('kids paddle')) return 'Kids paddle included - $0';
    if (lower.includes('pickleball')) return 'Pickleball included - $0';
    return '';
  };

  const createCartSpark = (x, y) => {
    const spark = document.createElement('div');
    spark.className = 'fg-cart-spark';
    spark.setAttribute('aria-hidden', 'true');
    spark.style.left = `${x}px`;
    spark.style.top = `${y}px`;

    const points = [
      [-28, -18],
      [26, -22],
      [34, 4],
      [20, 25],
      [-18, 27],
      [-34, 2],
      [-14, -31],
      [8, -34]
    ];

    points.forEach(([pointX, pointY], index) => {
      const point = document.createElement('span');
      point.style.setProperty('--fg-spark-x', `${pointX}px`);
      point.style.setProperty('--fg-spark-y', `${pointY}px`);
      point.style.setProperty('--fg-spark-delay', `${index * 18}ms`);
      spark.appendChild(point);
    });

    document.body.appendChild(spark);
    window.setTimeout(() => spark.remove(), 760);
  };

  const createTemporaryCartTarget = () => {
    const target = document.createElement('div');
    target.className = 'fg-cart-popout';
    target.setAttribute('aria-hidden', 'true');
    target.innerHTML = `
      <svg width="22" height="22" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M5 6h12l-1.3 7.2a2 2 0 0 1-2 1.7H7.1a2 2 0 0 1-2-1.7L3.8 2H1.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.5 18h.1M14 18h.1" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
      </svg>
    `;
    document.body.appendChild(target);
    window.requestAnimationFrame(() => target.classList.add('is-visible'));
    return target;
  };

  const animateProductToCart = (form, submitter, payload = {}) => new Promise((resolve) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      resolve();
      return;
    }

    const realCartTarget = document.querySelector('.fg-header .fg-icon-link[href$="/cart"], .fg-header .fg-icon-link[href*="/cart"], a[aria-label="Cart"]');
    const buttonRect = submitter ? submitter.getBoundingClientRect() : null;
    if (!buttonRect && !realCartTarget) {
      resolve();
      return;
    }

    const scope = form.closest('.fg-quick-add, .fg-pdp, .fg-product-card') || document;
    const sourceImage =
      form.querySelector('.fg-quick-add__head img') ||
      scope.querySelector('.fg-quick-add__head img') ||
      scope.querySelector('.fg-pdp-slide.is-active img, .fg-pdp__main-image img, [data-fg-gallery-slider] img') ||
      (submitter && submitter.closest('.fg-product-card') && submitter.closest('.fg-product-card').querySelector('.fg-product-card__media img')) ||
      document.querySelector('.fg-product-card__media img');

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const realTargetRect = realCartTarget ? realCartTarget.getBoundingClientRect() : null;
    const realTargetVisible = realTargetRect &&
      realTargetRect.width > 0 &&
      realTargetRect.height > 0 &&
      realTargetRect.bottom > 0 &&
      realTargetRect.right > 0 &&
      realTargetRect.top < viewportHeight &&
      realTargetRect.left < viewportWidth;
    const temporaryCartTarget = realTargetVisible ? null : createTemporaryCartTarget();
    const cartTarget = realTargetVisible ? realCartTarget : temporaryCartTarget;
    const targetRect = cartTarget.getBoundingClientRect();
    const sourceRect = sourceImage ? sourceImage.getBoundingClientRect() : (buttonRect || targetRect);
    const buttonCenterX = buttonRect ? buttonRect.left + buttonRect.width / 2 : sourceRect.left + sourceRect.width / 2;
    const buttonCenterY = buttonRect ? buttonRect.top + buttonRect.height / 2 : sourceRect.top + sourceRect.height / 2;
    const targetX = targetRect.left + targetRect.width / 2;
    const targetY = targetRect.top + targetRect.height / 2;
    const aspectRatio = sourceRect.width && sourceRect.height ? sourceRect.height / sourceRect.width : 1;
    const flyWidth = Math.min(122, Math.max(68, sourceRect.width * 0.28));
    const flyHeight = Math.min(132, Math.max(72, flyWidth * aspectRatio));
    const startX = buttonCenterX;
    const startY = buttonCenterY;
    const deltaX = targetX - startX;
    const deltaY = targetY - startY;
    const distance = Math.hypot(deltaX, deltaY);
    const arc = Math.min(220, Math.max(90, distance * 0.18));

    let flyer;
    if (sourceImage && sourceImage.currentSrc) {
      flyer = sourceImage.cloneNode(false);
      flyer.removeAttribute('srcset');
      flyer.removeAttribute('sizes');
      flyer.src = sourceImage.currentSrc;
      flyer.alt = '';
      flyer.className = 'fg-cart-flyer fg-cart-flyer--image';
    } else {
      flyer = document.createElement('div');
      flyer.textContent = payload.product_title || 'Added';
      flyer.className = 'fg-cart-flyer';
    }

    flyer.setAttribute('aria-hidden', 'true');
    flyer.style.left = `${startX - flyWidth / 2}px`;
    flyer.style.top = `${startY - flyHeight / 2}px`;
    flyer.style.width = `${flyWidth}px`;
    flyer.style.height = `${flyHeight}px`;

    document.body.appendChild(flyer);
    cartTarget.classList.add('is-cart-target');
    if (submitter) submitter.classList.add('is-releasing-product');

    const duration = 760;
    const rotationDirection = deltaX < 0 ? -1 : 1;
    const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    let startTime = 0;

    const move = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const rawProgress = Math.min(1, (timestamp - startTime) / duration);
      const progress = easeOutCubic(rawProgress);
      const releaseProgress = Math.min(1, rawProgress / 0.18);
      const travelShrink = Math.max(0, (rawProgress - 0.15) / 0.85);
      const shrinkProgress = easeOutExpo(travelShrink);
      const x = deltaX * progress;
      const y = (deltaY * progress) - (arc * Math.sin(Math.PI * progress));
      const launchScale = 0.32 + (0.78 * easeOutCubic(releaseProgress));
      const scale = Math.max(0.1, launchScale * (1 - (0.9 * shrinkProgress)));
      const rotate = rotationDirection * 12 * progress;
      const opacity = rawProgress > 0.88 ? Math.max(0, 1 - ((rawProgress - 0.88) / 0.12)) : 1;

      flyer.style.opacity = String(opacity);
      flyer.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale}) rotate(${rotate}deg)`;

      if (rawProgress < 1) {
        window.requestAnimationFrame(move);
        return;
      }

      flyer.remove();
      cartTarget.classList.remove('is-cart-target');
      if (submitter) submitter.classList.remove('is-releasing-product');
      createCartSpark(targetX, targetY);
      if (temporaryCartTarget) {
        temporaryCartTarget.classList.add('is-complete');
        window.setTimeout(() => temporaryCartTarget.remove(), 520);
      }
      resolve();
    };

    window.requestAnimationFrame(move);
  });

  const FG_FREE_SHIP_THRESHOLD = 6000; // $60.00 in cents

  const formatMoney = (cents) => `$${(Math.max(0, cents) / 100).toFixed(2)}`;

  const buildFreeShipBar = (cartTotal) => {
    if (typeof cartTotal !== 'number') return '';
    if (cartTotal >= FG_FREE_SHIP_THRESHOLD) {
      return `
        <div class="fg-cart-ship is-unlocked">
          <p>You've unlocked <strong>free shipping</strong>.</p>
          <i style="--fg-ship-progress:100%"></i>
        </div>`;
    }
    const remaining = FG_FREE_SHIP_THRESHOLD - cartTotal;
    const pct = Math.max(5, Math.round((cartTotal / FG_FREE_SHIP_THRESHOLD) * 100));
    return `
      <div class="fg-cart-ship">
        <p>You're <strong>${formatMoney(remaining)}</strong> away from free shipping.</p>
        <i style="--fg-ship-progress:${pct}%"></i>
      </div>`;
  };

  const showCartDrawer = async (payload, offer) => {
    const panel = ensurePanel('cart-drawer');
    const content = panel.querySelector('[data-fg-panel-content]');
    let cartCount = payload.quantity || 1;
    let cartTotal = null;

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (response.ok) {
        const cart = await response.json();
        cartCount = cart.item_count || cartCount;
        if (typeof cart.total_price === 'number') cartTotal = cart.total_price;
      }
    } catch (error) {
      // The drawer still works if the cart count endpoint is temporarily unavailable.
    }

    const giftLabel = getGiftLabel(offer || (payload.properties && payload.properties['Bundle offer']));
    const image = payload.image
      ? `<img src="${payload.image}" alt="${payload.product_title || 'Added product'}" loading="lazy">`
      : '';

    content.innerHTML = `
      <div class="fg-cart-drawer__success">Added to cart</div>
      <h2>You've added this to cart.</h2>
      ${buildFreeShipBar(cartTotal)}
      <div class="fg-cart-drawer__item">
        ${image}
        <div>
          <strong>${payload.product_title || 'Figureology product'}</strong>
          <span>${payload.variant_title && payload.variant_title !== 'Default Title' ? payload.variant_title : 'Ready for checkout'}</span>
          <small>Quantity: ${payload.quantity || 1}</small>
        </div>
      </div>
      ${giftLabel ? `<div class="fg-cart-drawer__gift"><span>Bundle gift</span><strong>${giftLabel}</strong></div>` : ''}
      <div class="fg-cart-upsell">
        <span class="fg-cart-upsell__label">Complete your kit</span>
        <div class="fg-cart-upsell__items">
          <a href="/products/pickleballs">+ Add pickleballs</a>
          <a href="/collections/accessories">+ Add a paddle cover</a>
        </div>
      </div>
      <div class="fg-cart-drawer__meta">${cartCount} item${cartCount === 1 ? '' : 's'} currently in cart</div>
      <div class="fg-cart-drawer__actions">
        <a class="fg-button fg-button--lime" href="/checkout">Checkout</a>
        <a class="fg-button fg-button--outline" href="/cart">View Cart</a>
      </div>
      <button class="fg-cart-drawer__continue" type="button" data-fg-close-panel>Continue shopping</button>
    `;

    root.classList.add('fg-cart-drawer-open');
  };

  const wireQuickAdd = () => {
    document.addEventListener('click', (event) => {
      const quickAddButton = event.target.closest('[data-fg-open-quick-add]');
      if (quickAddButton) {
        const card = quickAddButton.closest('.fg-product-card');
        const template = card && card.querySelector('[data-fg-quick-add-template]');
        if (!template) return;

        const panel = ensurePanel('quick-add');
        const content = panel.querySelector('[data-fg-panel-content]');
        content.innerHTML = '';
        content.appendChild(template.content.cloneNode(true));
        root.classList.add('fg-quick-add-open');
        return;
      }

      if (event.target.closest('[data-fg-close-panel]')) {
        closePanels();
      }

      const submitPdp = event.target.closest('[data-fg-submit-pdp]');
      if (submitPdp) {
        const form = document.querySelector('.fg-pdp-form');
        if (form) {
          form.requestSubmit();
        }
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePanels();
    });
  };

  const wireScrollMotion = () => {
    const movingItems = document.querySelectorAll('[data-fg-parallax]');
    if (!movingItems.length) return;

    let ticking = false;
    const update = () => {
      const viewport = window.innerHeight || 1;
      movingItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const progress = Math.min(1, Math.max(-1, (viewport * 0.5 - rect.top) / viewport));
        item.style.setProperty('--fg-parallax-y', `${progress * -18}px`);
      });
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });

    update();
  };

  const wireProductGallery = () => {
    const slider = document.querySelector('[data-fg-gallery-slider]');
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll('.fg-pdp-slide'));
    const dots = Array.from(document.querySelectorAll('.fg-pdp-dots span'));
    const thumbs = Array.from(document.querySelectorAll('[data-fg-gallery-thumb]'));

    const setActive = (index) => {
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === index));
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        const slide = slides[index];
        if (!slide) return;
        slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        setActive(index);
      });
    });

    let galleryTicking = false;
    slider.addEventListener('scroll', () => {
      if (galleryTicking || !slides.length) return;
      galleryTicking = true;
      window.requestAnimationFrame(() => {
        const index = Math.round(slider.scrollLeft / Math.max(1, slider.clientWidth));
        setActive(Math.min(slides.length - 1, Math.max(0, index)));
        galleryTicking = false;
      });
    }, { passive: true });
  };

  const wirePaddleFinder = () => {
    document.querySelectorAll('[data-fg-paddle-finder]').forEach((finder) => {
      const form = finder.querySelector('.fg-paddle-finder__form');
      const placeholder = finder.querySelector('[data-fg-finder-placeholder]');
      const cards = Array.from(finder.querySelectorAll('[data-fg-finder-result]'));
      const steps = Array.from(finder.querySelectorAll('[data-fg-finder-step]'));
      const progressButtons = Array.from(finder.querySelectorAll('[data-fg-finder-progress]'));
      const backButton = finder.querySelector('[data-fg-finder-back]');
      const nextButton = finder.querySelector('[data-fg-finder-next]');
      const submitButton = finder.querySelector('[data-fg-finder-submit]');
      const liveEmpty = finder.querySelector('[data-fg-live-empty]');
      const liveResult = finder.querySelector('[data-fg-live-result]');
      const liveTag = finder.querySelector('[data-fg-live-tag]');
      const liveName = finder.querySelector('[data-fg-live-name]');
      const liveScore = finder.querySelector('[data-fg-live-score]');
      const liveSub = finder.querySelector('[data-fg-live-sub]');
      const liveMatch = finder.querySelector('[data-fg-live-match]');
      const liveSteps = Array.from(finder.querySelectorAll('[data-fg-live-step]'));
      if (!form || !cards.length) return;

      const stepNames = ['level', 'priority', 'feel'];
      const liveMatches = {
        trainer: { tag: 'Youth / starter pick', name: 'Trainer Paddle (Youth)', score: 92, sub: 'Easy swing weight and forgiving control.', control: 72, power: 48, spin: 46, forgiveness: 94 },
        blossom: { tag: 'Beginner control pick', name: 'Blossom Graphite Paddle', score: 86, sub: 'Approachable control with standout style.', control: 78, power: 56, spin: 58, forgiveness: 86 },
        wave: { tag: 'Style-first power pick', name: 'Graphite Wave Paddle', score: 84, sub: 'Bold look with a more responsive graphite feel.', control: 70, power: 70, spin: 62, forgiveness: 78 },
        open: { tag: 'Control pick', name: 'Open Throat Sunrise Paddle', score: 89, sub: 'Quick hands and softer touch for kitchen play.', control: 92, power: 64, spin: 72, forgiveness: 78 },
        gen3: { tag: 'All-court pick', name: 'Gen 3 Carbon Fiber Paddle', score: 88, sub: 'Balanced power and control for cleaner rallies.', control: 82, power: 82, spin: 70, forgiveness: 74 },
        powerwave: { tag: 'Premium performance pick', name: 'Power Wave 18K Paddle', score: 93, sub: '18K carbon feel for faster, more confident play.', control: 86, power: 92, spin: 84, forgiveness: 76 },
        bigdog: { tag: 'Advanced power pick', name: 'Big Dog Paddle', score: 91, sub: 'Power-forward response for stronger drives.', control: 74, power: 96, spin: 78, forgiveness: 70 }
      };
      let activeStep = 0;
      let advanceTimer = 0;
      let liveUpdateTimer = 0;

      const getValue = (name) => {
        const input = form.querySelector(`[name="${name}"]:checked`);
        return input ? input.value : '';
      };

      const getRecommendation = () => {
        const level = getValue('level');
        const priority = getValue('priority');
        const feel = getValue('feel');

        if (!level || !priority || !feel) return '';
        if (level === 'kid') return 'trainer';
        if (level === 'beginner' && feel === 'easy') return 'trainer';
        if (level === 'beginner' && priority === 'power') return 'wave';
        if (level === 'beginner') return 'blossom';
        if (level === 'intermediate' && priority === 'control') return feel === 'premium' ? 'powerwave' : 'open';
        if (level === 'intermediate' && priority === 'power') return feel === 'premium' ? 'powerwave' : 'gen3';
        if (level === 'advanced' && priority === 'power') return feel === 'premium' ? 'bigdog' : 'powerwave';
        if (level === 'advanced') return feel === 'easy' && priority === 'control' ? 'open' : 'powerwave';
        if (feel === 'easy') return 'trainer';
        if (feel === 'balanced') return priority === 'power' ? 'gen3' : 'open';
        if (priority === 'control') return 'powerwave';
        if (priority === 'power') return 'bigdog';
        return 'powerwave';
      };

      const getLiveRecommendation = () => {
        const level = getValue('level');
        const priority = getValue('priority');
        const feel = getValue('feel');

        if (!level) return '';
        if (level === 'kid') return 'trainer';
        if (level === 'beginner') {
          if (feel === 'easy') return 'trainer';
          return priority === 'power' ? 'wave' : 'blossom';
        }
        if (level === 'intermediate') {
          if (priority === 'control') return 'open';
          if (priority === 'power') return feel === 'premium' ? 'powerwave' : 'gen3';
          return feel === 'premium' ? 'powerwave' : 'gen3';
        }
        if (level === 'advanced') {
          if (priority === 'control' && feel === 'easy') return 'open';
          if (priority === 'power' && feel === 'premium') return 'bigdog';
          return 'powerwave';
        }

        return '';
      };

      const updateLiveMatch = () => {
        const recommendation = getRecommendation() || getLiveRecommendation();
        const completedCount = stepNames.filter((name) => Boolean(getValue(name))).length;

        if (liveMatch) {
          liveMatch.classList.remove('is-updating');
          void liveMatch.offsetWidth;
          liveMatch.classList.add('is-updating');
          window.clearTimeout(liveUpdateTimer);
          liveUpdateTimer = window.setTimeout(() => liveMatch.classList.remove('is-updating'), 620);
        }

        liveSteps.forEach((step, index) => {
          const isDone = index < completedCount;
          const label = step.dataset.fgLiveLabel || step.textContent.replace(/\s*Done$/, '').trim();
          step.dataset.fgLiveLabel = label;
          step.classList.toggle('is-done', isDone);
          step.lastChild.textContent = isDone ? `${label} Done` : label;
        });

        if (!recommendation || !liveMatches[recommendation]) {
          if (liveEmpty) liveEmpty.hidden = false;
          if (liveResult) liveResult.hidden = true;
          return;
        }

        const match = liveMatches[recommendation];
        const partialPenalty = Math.max(0, 3 - completedCount) * 4;
        const displayedScore = Math.max(76, match.score - partialPenalty);

        if (liveEmpty) liveEmpty.hidden = true;
        if (liveResult) liveResult.hidden = false;
        if (liveTag) liveTag.textContent = match.tag;
        if (liveName) liveName.textContent = match.name;
        if (liveScore) liveScore.textContent = `${displayedScore}%`;
        if (liveSub) liveSub.textContent = completedCount < 3 ? 'Match based on your answers so far.' : match.sub;

        ['control', 'power', 'spin', 'forgiveness'].forEach((stat) => {
          const bar = finder.querySelector(`[data-fg-live-bar="${stat}"]`);
          if (bar) bar.style.width = `${match[stat]}%`;
        });
      };

      const showRecommendation = () => {
        const recommendation = getRecommendation();
        if (!recommendation) return;

        if (placeholder) placeholder.hidden = true;
        finder.classList.remove('has-fresh-result');
        void finder.offsetWidth;
        finder.classList.add('has-result');
        finder.classList.add('has-fresh-result');
        cards.forEach((card) => {
          card.classList.toggle('is-active', card.dataset.fgFinderResult === recommendation);
        });
      };

      const isStepComplete = (index = activeStep) => Boolean(getValue(stepNames[index]));

      const arePreviousStepsComplete = (targetIndex) => stepNames
        .slice(0, targetIndex)
        .every((name) => Boolean(getValue(name)));

      const markCurrentStep = () => {
        const current = steps[activeStep];
        if (!current) return;

        current.classList.remove('is-shaking');
        // Restart the animation when a user tries to continue without answering.
        void current.offsetWidth;
        current.classList.add('is-shaking');
        window.setTimeout(() => current.classList.remove('is-shaking'), 420);
      };

      const updateStep = (nextIndex) => {
        if (!steps.length) return;
        activeStep = Math.max(0, Math.min(steps.length - 1, nextIndex));
        finder.style.setProperty('--fg-finder-progress', `${((activeStep + 1) / steps.length) * 100}%`);
        finder.dataset.fgFinderCurrentStep = String(activeStep + 1);

        steps.forEach((step, index) => {
          step.hidden = index !== activeStep;
          step.classList.toggle('is-active', index === activeStep);
          step.classList.toggle('is-complete', isStepComplete(index));
        });

        progressButtons.forEach((button, index) => {
          button.classList.toggle('is-active', index === activeStep);
          button.classList.toggle('is-complete', index < activeStep || isStepComplete(index));
          button.disabled = index > activeStep && !arePreviousStepsComplete(index);
        });

        if (backButton) backButton.disabled = activeStep === 0;
        if (nextButton) {
          nextButton.hidden = activeStep === steps.length - 1;
          nextButton.disabled = !isStepComplete();
        }
        if (submitButton) {
          submitButton.hidden = activeStep !== steps.length - 1;
          submitButton.disabled = !isStepComplete();
        }
        updateLiveMatch();
      };

      const advanceIfReady = () => {
        if (!steps.length || !isStepComplete()) return;

        if (getRecommendation()) {
          updateLiveMatch();
          showRecommendation();
          updateStep(activeStep);
          return;
        }

        if (activeStep < steps.length - 1) {
          window.clearTimeout(advanceTimer);
          advanceTimer = window.setTimeout(() => updateStep(activeStep + 1), 260);
        }
      };

      if (steps.length) {
        updateStep(0);
      }

      if (backButton) {
        backButton.addEventListener('click', () => updateStep(activeStep - 1));
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          if (!isStepComplete()) {
            markCurrentStep();
            return;
          }

          updateStep(activeStep + 1);
        });
      }

      progressButtons.forEach((button, index) => {
        button.addEventListener('click', () => {
          if (index <= activeStep || arePreviousStepsComplete(index)) {
            updateStep(index);
          } else {
            markCurrentStep();
          }
        });
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (steps.length && !isStepComplete()) {
          markCurrentStep();
          return;
        }
        updateLiveMatch();
        showRecommendation();
      });

      form.addEventListener('change', () => {
        finder.classList.add('is-live');
        finder.classList.remove('has-result');
        cards.forEach((card) => card.classList.remove('is-active'));
        updateStep(activeStep);
        advanceIfReady();
      });
    });
  };

  const wireAjaxCart = () => {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-fg-product-form], .fg-product__form');
      if (!form) return;

      const submitter = event.submitter;
      if (submitter && submitter.closest('.shopify-payment-button')) return;

      const variantInput = form.querySelector('[name="id"]:checked') || form.querySelector('[name="id"]');
      if (!variantInput || !variantInput.value) return;

      event.preventDefault();
      const bundleOffer = updateBundleProperty(form);

      const button = submitter && submitter.matches('button') ? submitter : form.querySelector('button[type="submit"]');
      const originalText = button ? button.textContent : '';
      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
        button.textContent = 'Adding...';
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });

        const responseText = await response.text();
        let payload = {};

        try {
          payload = JSON.parse(responseText);
        } catch (error) {
          payload = {};
        }

        if (!response.ok) throw new Error(payload.description || 'Could not add this product.');

        await animateProductToCart(form, button, payload);
        root.classList.add('fg-cart-pulse');
        window.setTimeout(() => root.classList.remove('fg-cart-pulse'), 700);
        closePanels();
        await showCartDrawer(payload, bundleOffer);
        showToast(`${payload.product_title || 'Product'} added to cart`);
      } catch (error) {
        showToast(error.message || 'Could not add this product. Please try again.');
      } finally {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
          button.textContent = originalText;
        }
      }
    });
  };

  const wireEmailPopup = () => {
    const popup = document.querySelector('[data-fg-email-popup]');
    if (!popup) return;

    const storageKey = 'figureologyEmailPopupClosed';
    const canStore = (() => {
      try {
        window.localStorage.setItem('__fg_test', '1');
        window.localStorage.removeItem('__fg_test');
        return true;
      } catch (error) {
        return false;
      }
    })();

    const close = () => {
      root.classList.remove('fg-email-popup-open');
      popup.setAttribute('aria-hidden', 'true');
      if (canStore) window.localStorage.setItem(storageKey, '1');
    };

    if (canStore && window.localStorage.getItem(storageKey)) return;

    popup.querySelectorAll('[data-fg-email-close]').forEach((button) => {
      button.addEventListener('click', close);
    });

    const form = popup.querySelector('form');
    if (form) form.addEventListener('submit', () => {
      if (canStore) window.localStorage.setItem(storageKey, '1');
    });

    window.setTimeout(() => {
      if (
        root.classList.contains('fg-quick-add-open') ||
        root.classList.contains('fg-cart-drawer-open') ||
        root.classList.contains('fg-menu-open') ||
        root.classList.contains('fg-search-open')
      ) return;
      root.classList.add('fg-email-popup-open');
      popup.setAttribute('aria-hidden', 'false');
    }, 7000);
  };

  document.addEventListener('DOMContentLoaded', () => {
    setHeaderState();
    reveal();
    wireHeaderPanels();
    wireCollectionSort();
    wireCollectionFilters();
    wireQuickAdd();
    wireProductGallery();
    wirePaddleFinder();
    wireAjaxCart();
    wireScrollMotion();
    wirePremiumMotion();
    wireEmailPopup();
    window.addEventListener('scroll', setHeaderState, { passive: true });
  });
})();
